import * as XLSX from 'xlsx'
import moment from 'moment-timezone'

/**
 * Exportación a Excel compartida por las vistas de reportes.
 *
 * Las cuatro vistas exportan lo mismo conceptualmente —una o varias tablas ya
 * visibles en pantalla— así que la conversión vive aquí en vez de repetirse.
 * Se exporta lo que el usuario está viendo (ya filtrado por el backend), no una
 * segunda consulta: el archivo y la pantalla nunca se contradicen.
 */

/**
 * Mapea filas crudas a filas con encabezados en español.
 * @param {Array} rows Filas del reporte
 * @param {Array} columns [{ key, label, format }]
 * @return {Array} Filas listas para la hoja
 */
const toSheetRows = (rows, columns) =>
  (rows || []).map(row =>
    columns.reduce((out, col) => {
      const value = row[col.key]
      out[col.label] = col.format ? col.format(value, row) : value // eslint-disable-line no-param-reassign
      return out
    }, {})
  )

/**
 * Ancho de columna aproximado según el contenido, para que el archivo se pueda
 * leer sin tener que ajustar columnas a mano.
 * @param {Array} sheetRows Filas ya mapeadas
 * @param {Array} columns Definición de columnas
 * @return {Array} Anchos para `ws['!cols']`
 */
const columnWidths = (sheetRows, columns) =>
  columns.map(col => {
    const longest = sheetRows.reduce((max, row) => {
      const cell = row[col.label]
      const length =
        cell === null || cell === undefined ? 0 : String(cell).length
      return Math.max(max, length)
    }, col.label.length)
    return { wch: Math.min(Math.max(longest + 2, 10), 60) }
  })

/**
 * Genera y descarga un .xlsx con una o más hojas.
 * @param {String} fileName Nombre base, sin extensión ni fecha
 * @param {Array} sheets [{ name, rows, columns }]
 */
export default function exportReport(fileName, sheets) {
  const workbook = XLSX.utils.book_new()

  sheets
    .filter(sheet => sheet.rows && sheet.rows.length)
    .forEach(sheet => {
      const sheetRows = toSheetRows(sheet.rows, sheet.columns)
      const worksheet = XLSX.utils.json_to_sheet(sheetRows)
      worksheet['!cols'] = columnWidths(sheetRows, sheet.columns)
      // Excel corta los nombres de hoja en 31 caracteres.
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name.slice(0, 31))
    })

  if (!workbook.SheetNames.length) return false

  const stamp = moment().format('YYYY-MM-DD')
  XLSX.writeFile(workbook, `${fileName}-${stamp}.xlsx`)
  return true
}
